import React from 'react';
import { Box, Paper, Typography, Divider, Grid, Button, Dialog } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { useKioskContext } from '../context/KioskContext';

const Receipt = ({ open, onClose, orderDetails }) => {
  const { formatPrice } = useKioskContext();
  
  // Format date for receipt
  const today = new Date();
  const formattedDate = `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear()}`;
  const formattedTime = today.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  
  // Generate receipt number
  const receiptNumber = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}-${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`;
  
  // Simulate printing receipt
  const handlePrint = () => {
    const printContent = document.getElementById('receipt-to-print');
    const printWindow = window.open('', '', 'height=600,width=400');
    
    printWindow.document.write('<html><head><title>Receipt</title>');
    printWindow.document.write('<style>');
    printWindow.document.write(`
      body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
      .receipt { width: 100%; max-width: 300px; margin: 0 auto; }
      .receipt-header { text-align: center; margin-bottom: 10px; }
      .receipt-line { border-top: 1px dashed #000; margin: 10px 0; }
      .receipt-item { display: flex; justify-content: space-between; margin: 5px 0; }
      .receipt-total { font-weight: bold; margin-top: 10px; }
    `);
    printWindow.document.write('</style>');
    printWindow.document.write('</head><body>');
    printWindow.document.write(printContent.innerHTML);
    printWindow.document.write('</body></html>');
    
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, p: 2 }
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" align="center" gutterBottom>Receipt</Typography>
        
        <Paper 
          elevation={0} 
          sx={{ p: 3, border: '1px solid #ddd' }}
          id="receipt-to-print"
        >
          {/* Receipt Header */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              STAR SIAM FOODS CORPORATION
            </Typography>
            <Typography variant="body2">
              VAT Reg. TIN: 435-611-372-00000
            </Typography>
            <Typography variant="body2">
              Solito Bldg. West Service Road cor. Nagnilan Ave.
            </Typography>
            <Typography variant="body2">
              Barangay Greater Lagro, 1100 Quezon City
            </Typography>
          </Box>
          
          <Divider sx={{ mb: 2 }} />
          
          {/* Receipt Info */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                SERVICE INVOICE
              </Typography>
              <Typography variant="body2">
                No. {receiptNumber}
              </Typography>
              <Typography variant="body2">
                Date: {formattedDate} {formattedTime}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                ☑ CASH SALES
              </Typography>
              <Typography variant="body2">
                ☐ CHARGE SALES
              </Typography>
            </Box>
          </Box>
          
          {/* Customer Info */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>SOLD TO:</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Customer Name: {orderDetails?.customerName || ""}
            </Typography>
            <Typography variant="body2">
              TIN: 
            </Typography>
            <Typography variant="body2">
              Address: 
            </Typography>
          </Box>
          
          {/* Order Items */}
          <Box sx={{ mb: 2, border: '1px solid #ddd' }}>
            <Grid container sx={{ p: 1, borderBottom: '1px solid #ddd', bgcolor: '#f5f5f5' }}>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  ITEM DESCRIPTION / NATURE OF SERVICE
                </Typography>
              </Grid>
              <Grid item xs={2} sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Quantity
                </Typography>
              </Grid>
              <Grid item xs={2} sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Unit Price
                </Typography>
              </Grid>
              <Grid item xs={2} sx={{ textAlign: 'right' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  AMOUNT
                </Typography>
              </Grid>
            </Grid>
            
            {/* Order Items */}
            <Box sx={{ p: 1, minHeight: '150px' }}>
              {orderDetails?.items?.map((item, index) => (
                <Grid container key={index} sx={{ mb: 1 }}>
                  <Grid item xs={6}>
                    <Typography variant="body2">{item.menuName}</Typography>
                    {item.selectedOptions?.map((option, i) => (
                      <Typography variant="caption" display="block" key={i} sx={{ ml: 1 }}>
                        • {typeof option.choice === 'object' ? option.choice.name : option.choice}
                      </Typography>
                    ))}
                  </Grid>
                  <Grid item xs={2} sx={{ textAlign: 'center' }}>
                    <Typography variant="body2">{item.quantity}</Typography>
                  </Grid>
                  <Grid item xs={2} sx={{ textAlign: 'center' }}>
                    <Typography variant="body2">{formatPrice(item.totalPrice / item.quantity)}</Typography>
                  </Grid>
                  <Grid item xs={2} sx={{ textAlign: 'right' }}>
                    <Typography variant="body2">{formatPrice(item.totalPrice)}</Typography>
                  </Grid>
                </Grid>
              ))}
            </Box>
          </Box>
          
          {/* Tax Info */}
          <Grid container spacing={0} sx={{ mb: 3 }}>
            <Grid item xs={6} sx={{ border: '1px solid #ddd', p: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                VATable Sales
              </Typography>
              <Typography variant="body2">
                Zero-Rated Sales: {formatPrice(0)}
              </Typography>
              <Typography variant="body2">
                VAT-Exempt Sales: {formatPrice(0)}
              </Typography>
              <Typography variant="body2">
                12% VAT: {formatPrice(orderDetails?.vatAmount || 0)}
              </Typography>
            </Grid>
            <Grid item xs={6} sx={{ border: '1px solid #ddd', p: 1, borderLeft: 'none' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Amount Net of VAT
              </Typography>
              <Typography variant="body2">
                {formatPrice(orderDetails?.subtotalExcludingVAT || 0)}
              </Typography>
              <Typography variant="body2" sx={{ mt: 2 }}>
                Add: 12% VAT
              </Typography>
              <Typography variant="body2">
                {formatPrice(orderDetails?.vatAmount || 0)}
              </Typography>
            </Grid>
          </Grid>
          
          {/* Total and Discount */}
          <Box sx={{ textAlign: 'right', mb: 2 }}>
            {orderDetails?.discount > 0 && (
              <Typography variant="body2">
                Discount: -{formatPrice(orderDetails.discount)}
              </Typography>
            )}
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              TOTAL AMOUNT DUE: {formatPrice(orderDetails?.finalTotal || 0)}
            </Typography>
            
            {/* Payment Details */}
            {orderDetails?.amountPaid > 0 && (
              <>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Amount Paid: {formatPrice(orderDetails.amountPaid)}
                </Typography>
                {orderDetails?.change > 0 && (
                  <Typography variant="body2">
                    Change: {formatPrice(orderDetails.change)}
                  </Typography>
                )}
              </>
            )}
          </Box>
          
          {/* Payment Info */}
          <Box sx={{ border: '1px solid #ddd', p: 1, mb: 2 }}>
            <Typography variant="body2">
              Received the above items in good order:
            </Typography>
            <Box sx={{ mt: 3, textAlign: 'right' }}>
              <Typography variant="body2" sx={{ borderTop: '1px solid #999', display: 'inline-block', pt: 1, minWidth: '200px' }}>
                Customer/Authorized Representative
              </Typography>
            </Box>
          </Box>
          
          {/* Footer */}
          <Box sx={{ textAlign: 'center', fontSize: '10px', color: '#666' }}>
            <Typography variant="caption" display="block">
              THIS INVOICE/RECEIPT SHALL BE VALID FOR FIVE (5) YEARS FROM THE DATE OF
            </Typography>
            <Typography variant="caption" display="block">
              BIR APPROVAL: March 2025
            </Typography>
            <Typography variant="caption" display="block">
              Approved Series: 00001-99999 (IRIS) PTU: 01-21
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              
            </Typography>
          </Box>
        </Paper>
        
        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
          <Button 
            variant="contained" 
            startIcon={<PrintIcon />}
            onClick={handlePrint}
          >
            Print Receipt
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default Receipt;
